import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import EmployeeDrawer from './EmployeeDrawer';
import { resolveWorkforceScope } from './workforceBusinessUnitUtils';
import EmployeeImportDialog from './employeeimport';
import { formatDateOnly, getFormatConfig, initializeFormats } from '../../../utils/global';
import { buildLookupListUrl, parseLookupItems } from './workforcePositionUtils';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonatePlainIconButtonSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

// Toolbar action icons on the page gradient use white plain icon button styles.

const EMPLOYMENT_TYPE_LIST_CODE = 'EMPLOYMENT_TYPE';
const FALLBACK_EMPLOYMENT_TYPES = [
  { code: 'REG_FT', label: 'Reg FT' },
  { code: 'RPT_W/O', label: 'RPT W/O' },
];

const getRolePositionId = (role) => role?.rolePositionId || role?.id || '';

const getRolePositionLabel = (role) =>
  role?.roleName || role?.role_name || role?.roleCode || getRolePositionId(role) || 'Position';

const formatHourlyMoney = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
};

/** Default availability for new employees (all days); matches drawer "AVAILABLE" shape. */
const DEFAULT_EMPLOYEE_AVAILABILITY = {
  available: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
};

const buildEmployeeColumns = (dateFormat, employmentTypeOptions = []) => [
  { field: 'employee_code', headerName: 'Employee Code', width: 140, resizable: true },
  { field: 'first_name', headerName: 'First Name', width: 120, resizable: true },
  { field: 'last_name', headerName: 'Last Name', width: 120, resizable: true },
  { field: 'phone', headerName: 'Mobile Phone', width: 140, resizable: true },
  { field: 'home_phone', headerName: 'Home Phone', width: 140, resizable: true },
  {
    field: 'hire_date',
    headerName: 'Hire Date',
    width: 120,
    resizable: true,
    valueFormatter: (params) => (params.value ? formatDateOnly(params.value, dateFormat) : ''),
  },
  {
    field: 'employment_type',
    headerName: 'Employment Type',
    width: 150,
    resizable: true,
    valueFormatter: (params) => {
      const raw = params.value || '';
      const match = employmentTypeOptions.find(
        (opt) => opt.code === raw || opt.label === raw || opt.code === String(raw).replace(/ /g, '_')
      );
      return match?.label || raw;
    },
  },
  { field: 'status', headerName: 'Status', width: 120, resizable: true },
  {
    field: 'hourly_rate',
    headerName: 'Hourly Rate',
    width: 120,
    resizable: true,
    valueFormatter: (params) => formatHourlyMoney(params.value),
  },
  { field: 'department', headerName: 'Department', width: 130, resizable: true },
  { field: 'jobcode_level_1', headerName: 'Jobcode Level 1', width: 150, resizable: true },
  { field: 'jobcode_level_2', headerName: 'Jobcode Level 2', width: 150, resizable: true },
];

const Employee = ({ onBack, selectedBusinessUnit, onEmployeeDataChanged }) => {
  const { canAdd, canEdit, canDelete, canUpload } = useFunctionPermissions(FC.WORKFORCE_OPTIMIZATION);
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const tenantId = localStorage.getItem('tenant_id') || '';
  const username = localStorage.getItem('username') || '';
  const {
    propertyId: selectedPropertyId,
    businessUnitName: selectedBusinessUnitName,
    label: selectedBusinessUnitLabel,
    jobCodeLevel2: selectedJobCodeLevel2,
  } = resolveWorkforceScope(selectedBusinessUnit);

  const [formatConfig, setFormatConfig] = useState(getFormatConfig());
  const [rows, setRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowsPendingDelete, setRowsPendingDelete] = useState([]);
  const [employeeDrawerOpen, setEmployeeDrawerOpen] = useState(false);
  const [selectedDrawerEmployeeRow, setSelectedDrawerEmployeeRow] = useState(null);
  const [employeeImportOpen, setEmployeeImportOpen] = useState(false);
  const [roleMasters, setRoleMasters] = useState([]);
  const [roleMastersLoading, setRoleMastersLoading] = useState(false);
  const [employmentTypeOptions, setEmploymentTypeOptions] = useState(FALLBACK_EMPLOYMENT_TYPES);
  const [form, setForm] = useState({
    external_employee_id: '',
    employee_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    home_phone: '',
    primary_property_id: '',
    hire_date: '',
    termination_date: '',
    employment_type: '',
    status: '',
    hourly_rate: '',
    hourly_rate_adjustment: '',
    default_role_id: '',
    department: '',
    department_code: '',
    jobcode_level_1: '',
    jobcode_level_2: '',
    manager_id: '',
    max_hours_per_week: '',
    preferred_shift_type: '',
    is_overtime_allowed: '',
    is_salaried: 'NO',
    annual_salary: '',
    user_id: '',
    notes: '',
    availability: DEFAULT_EMPLOYEE_AVAILABILITY,
    created_by: username,
    updated_by: username,
  });

  const hasSelectedRow = selectionModel.length > 0;
  const hasSingleSelection = selectionModel.length === 1;
  const selectedRows = useMemo(
    () => rows.filter((row) => selectionModel.includes(row.id)),
    [rows, selectionModel]
  );

  useEffect(() => {
    if (!tenantId) return;
    initializeFormats(tenantId).then(() => setFormatConfig(getFormatConfig()));
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    const loadEmploymentTypes = async () => {
      if (!tenantId) {
        setEmploymentTypeOptions(FALLBACK_EMPLOYMENT_TYPES);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          buildLookupListUrl(process.env.REACT_APP_BACKEND_URL, EMPLOYMENT_TYPE_LIST_CODE, tenantId),
          {
            method: 'GET',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'Content-Type': 'application/json',
            },
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to load employment types: ${response.status}`);
        }
        const data = await response.json();
        const items = parseLookupItems(data?.list_items ?? data?.listItems)
          .map((item) => ({
            code: String(item?.code || '').trim(),
            label: String(item?.label || item?.code || '').trim(),
          }))
          .filter((item) => item.code);
        if (!cancelled) {
          setEmploymentTypeOptions(items.length ? items : FALLBACK_EMPLOYMENT_TYPES);
        }
      } catch (error) {
        console.error('Error loading employment types from lookup:', error);
        if (!cancelled) {
          setEmploymentTypeOptions(FALLBACK_EMPLOYMENT_TYPES);
        }
      }
    };
    loadEmploymentTypes();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const employeeColumns = useMemo(
    () => buildEmployeeColumns(formatConfig.DateFormat, employmentTypeOptions),
    [formatConfig.DateFormat, employmentTypeOptions]
  );

  const roleMasterIds = useMemo(
    () => new Set(roleMasters.map((r) => String(getRolePositionId(r))).filter(Boolean)),
    [roleMasters]
  );

  const unknownDefaultRoleId = useMemo(() => {
    const id = String(form.default_role_id || '').trim();
    if (!id || roleMasterIds.has(id)) return null;
    return id;
  }, [form.default_role_id, roleMasterIds]);

  const defaultsFromRoleId = (roleId) => {
    const id = String(roleId || '').trim();
    if (!id) {
      return { department: '', department_code: '', jobcode_level_1: '' };
    }
    const role = roleMasters.find((item) => String(getRolePositionId(item)) === id);
    if (!role) return null;
    return {
      department: role.department || '',
      department_code: role.departmentCode || role.department_code || '',
      jobcode_level_1: role.roleCode || role.role_code || '',
    };
  };

  useEffect(() => {
    if (!openDialog) return;
    const fromRole = defaultsFromRoleId(form.default_role_id);
    if (!fromRole) return;
    setForm((prev) => {
      if (
        prev.department === fromRole.department &&
        prev.department_code === fromRole.department_code &&
        prev.jobcode_level_1 === fromRole.jobcode_level_1
      ) {
        return prev;
      }
      return {
        ...prev,
        department: fromRole.department,
        department_code: fromRole.department_code,
        jobcode_level_1: fromRole.jobcode_level_1,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, form.default_role_id, roleMasters]);

  useEffect(() => {
    if (!openDialog) return;
    const level2 = selectedJobCodeLevel2 || '';
    setForm((prev) => {
      if (prev.jobcode_level_2 === level2) return prev;
      return { ...prev, jobcode_level_2: level2 };
    });
  }, [openDialog, selectedJobCodeLevel2]);

  useEffect(() => {
    let cancelled = false;
    const loadRolePositions = async () => {
      if (!tenantId || !selectedPropertyId) {
        setRoleMasters([]);
        setRoleMastersLoading(false);
        return;
      }
      try {
        setRoleMastersLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/role-positions/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(selectedPropertyId)}`,
          {
            method: 'GET',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'Content-Type': 'application/json',
            },
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch positions: ${response.status}`);
        }
        const data = await response.json();
        if (!cancelled) {
          setRoleMasters(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error loading role positions:', error);
        if (!cancelled) {
          setRoleMasters([]);
        }
      } finally {
        if (!cancelled) {
          setRoleMastersLoading(false);
        }
      }
    };
    loadRolePositions();
    return () => {
      cancelled = true;
    };
  }, [tenantId, selectedPropertyId]);

  const mapApiEmployeeToRow = (employee) => ({
    id: employee.employeeId,
    employee_uuid: employee.employeeId,
    employee_id: employee.employeeCode || '',
    tenant_id: employee.tenantId || '',
    external_employee_id: employee.externalEmployeeId || '',
    employee_code: employee.employeeCode || '',
    first_name: employee.firstName || '',
    last_name: employee.lastName || '',
    full_name: employee.fullName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
    email: employee.email || '',
    phone: employee.phone || '',
    home_phone: employee.homePhone || '',
    primary_property_id: employee.primaryPropertyId || '',
    hire_date: employee.hireDate || '',
    termination_date: employee.terminationDate || '',
    employment_type: employee.employmentType || '',
    status: employee.status || '',
    hourly_rate: employee.hourlyRate ?? '',
    hourly_rate_adjustment: employee.hourlyRateAdjustment ?? '',
    default_role_id: employee.defaultRoleId || '',
    department: employee.department || '',
    department_code: employee.departmentCode || employee.department_code || '',
    jobcode_level_1: employee.jobcodeLevel1 || '',
    jobcode_level_2: employee.jobcodeLevel2 || '',
    manager_id: employee.managerId || '',
    max_hours_per_week: employee.maxHoursPerWeek ?? '',
    preferred_shift_type: employee.preferredShiftType || '',
    is_overtime_allowed: employee.isOvertimeAllowed === true ? 'YES' : employee.isOvertimeAllowed === false ? 'NO' : '',
    is_salaried: employee.isSalaried === true ? 'YES' : 'NO',
    annual_salary: employee.annualSalary ?? '',
    user_id: employee.userId || '',
    notes: employee.notes || '',
    availability: employee.availability ?? null,
    created_at: employee.createdAt || '',
    updated_at: employee.updatedAt || '',
    created_by: employee.createdBy || '',
    updated_by: employee.updatedBy || '',
  });

  const normalizeDateTime = (value) => {
    if (!value) return null;
    if (value.includes(' ')) return value;
    return `${value} 00:00:00`;
  };

  const buildEmployeePayload = (currentForm, existingEmployeeId) => {
    const isSalaried = currentForm.is_salaried === 'YES';
    return {
    tenantId,
    externalEmployeeId: currentForm.external_employee_id || null,
    employeeCode: currentForm.employee_code || null,
    firstName: currentForm.first_name || null,
    lastName: currentForm.last_name || null,
    email: currentForm.email || null,
    phone: currentForm.phone || null,
    homePhone: currentForm.home_phone || null,
    primaryPropertyId: currentForm.primary_property_id || null,
    hireDate: normalizeDateTime(currentForm.hire_date),
    terminationDate: normalizeDateTime(currentForm.termination_date),
    employmentType: currentForm.employment_type || null,
    status: currentForm.status || null,
    hourlyRate: isSalaried ? 0 : (currentForm.hourly_rate === '' ? 0 : Number(currentForm.hourly_rate)),
    hourlyRateAdjustment: isSalaried ? 0 : (currentForm.hourly_rate_adjustment === '' ? 0 : Number(currentForm.hourly_rate_adjustment)),
    defaultRoleId: currentForm.default_role_id || null,
    department: currentForm.department || null,
    departmentCode: currentForm.department_code || null,
    jobcodeLevel1: currentForm.jobcode_level_1 || null,
    jobcodeLevel2: currentForm.jobcode_level_2 || null,
    managerId: currentForm.manager_id || null,
    maxHoursPerWeek: currentForm.max_hours_per_week === '' ? null : Number(currentForm.max_hours_per_week),
    preferredShiftType: currentForm.preferred_shift_type || null,
    isOvertimeAllowed: isSalaried ? false : (currentForm.is_overtime_allowed === '' ? null : currentForm.is_overtime_allowed === 'YES'),
    isSalaried,
    annualSalary: isSalaried
      ? (currentForm.annual_salary === '' ? null : Number(currentForm.annual_salary))
      : null,
    userId: currentForm.user_id || null,
    notes: currentForm.notes || null,
    availability: existingEmployeeId
      ? currentForm.availability ?? null
      : currentForm.availability ?? DEFAULT_EMPLOYEE_AVAILABILITY,
    createdBy: existingEmployeeId ? (currentForm.created_by || username) : username,
    updatedBy: username,
    ...(existingEmployeeId ? { employeeId: existingEmployeeId } : {}),
  };
  };

  const fetchEmployees = async () => {
    if (!tenantId || !selectedPropertyId) {
      setRows([]);
      return;
    }
    try {
      setLoadingEmployees(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/employees/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(selectedPropertyId)}`,
        {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to fetch employees: ${response.status}`);
      }
      const data = await response.json();
      setRows(Array.isArray(data) ? data.map(mapApiEmployeeToRow) : []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setRows([]);
      showError(error.message || 'Failed to fetch employees');
    } finally {
      setLoadingEmployees(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [tenantId, selectedPropertyId]);

  const handleOpenDialog = () => {
    if (!canAdd) return;
    setIsEditMode(false);
    setEditingEmployeeId(null);
    setForm({
      external_employee_id: '',
      employee_code: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      home_phone: '',
      primary_property_id: '',
      hire_date: '',
      termination_date: '',
      employment_type: '',
      status: '',
      hourly_rate: '',
      hourly_rate_adjustment: '',
      default_role_id: '',
      department: '',
      department_code: '',
      jobcode_level_1: '',
      jobcode_level_2: '',
      manager_id: '',
      max_hours_per_week: '',
      preferred_shift_type: '',
      is_overtime_allowed: '',
      is_salaried: 'NO',
      annual_salary: '',
      user_id: '',
      notes: '',
      availability: DEFAULT_EMPLOYEE_AVAILABILITY,
      created_by: username,
      updated_by: username,
    });
    setForm((prev) => ({ ...prev, primary_property_id: selectedPropertyId }));
    setOpenDialog(true);
  };

  const handleEditSelected = () => {
    if (!canEdit || !hasSingleSelection) return;
    const selectedId = selectionModel[0];
    const selectedRow = rows.find((row) => row.id === selectedId);
    if (!selectedRow?.employee_uuid) return;

    setIsEditMode(true);
    setEditingEmployeeId(selectedRow.employee_uuid);
    setForm({
      external_employee_id: selectedRow.external_employee_id || '',
      employee_code: selectedRow.employee_code || '',
      first_name: selectedRow.first_name || '',
      last_name: selectedRow.last_name || '',
      email: selectedRow.email || '',
      phone: selectedRow.phone || '',
      home_phone: selectedRow.home_phone || '',
      primary_property_id: selectedRow.primary_property_id || '',
      hire_date: (selectedRow.hire_date || '').split(' ')[0],
      termination_date: (selectedRow.termination_date || '').split(' ')[0],
      employment_type: selectedRow.employment_type || '',
      status: selectedRow.status || '',
      hourly_rate: selectedRow.hourly_rate ?? '',
      hourly_rate_adjustment: selectedRow.hourly_rate_adjustment ?? '',
      default_role_id: selectedRow.default_role_id || '',
      department: selectedRow.department || '',
      department_code: selectedRow.department_code || '',
      jobcode_level_1: selectedRow.jobcode_level_1 || '',
      jobcode_level_2: selectedRow.jobcode_level_2 || '',
      manager_id: selectedRow.manager_id || '',
      max_hours_per_week: selectedRow.max_hours_per_week ?? '',
      preferred_shift_type: selectedRow.preferred_shift_type || '',
      is_overtime_allowed: selectedRow.is_overtime_allowed || '',
      is_salaried: selectedRow.is_salaried || 'NO',
      annual_salary: selectedRow.annual_salary ?? '',
      user_id: selectedRow.user_id || '',
      notes: selectedRow.notes || '',
      availability: selectedRow.availability ?? null,
      created_by: selectedRow.created_by || '',
      updated_by: username,
    });
    setForm((prev) => ({ ...prev, primary_property_id: selectedPropertyId || prev.primary_property_id }));
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEditMode(false);
    setEditingEmployeeId(null);
    setForm({
      external_employee_id: '',
      employee_code: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      home_phone: '',
      primary_property_id: '',
      hire_date: '',
      termination_date: '',
      employment_type: '',
      status: '',
      hourly_rate: '',
      hourly_rate_adjustment: '',
      default_role_id: '',
      department: '',
      department_code: '',
      jobcode_level_1: '',
      jobcode_level_2: '',
      manager_id: '',
      max_hours_per_week: '',
      preferred_shift_type: '',
      is_overtime_allowed: '',
      is_salaried: 'NO',
      annual_salary: '',
      user_id: '',
      notes: '',
      availability: DEFAULT_EMPLOYEE_AVAILABILITY,
      created_by: username,
      updated_by: username,
    });
    setForm((prev) => ({ ...prev, primary_property_id: selectedPropertyId }));
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'default_role_id') {
        if (!value) {
          next.department = '';
          next.department_code = '';
          next.jobcode_level_1 = '';
        } else {
          const role = roleMasters.find((item) => String(getRolePositionId(item)) === String(value));
          if (role) {
            next.department = role.department || '';
            next.department_code = role.departmentCode || role.department_code || '';
            next.jobcode_level_1 = role.roleCode || role.role_code || '';
          }
        }
      }
      if (name === 'is_salaried' && value === 'YES') {
        next.is_overtime_allowed = 'NO';
        next.hourly_rate = '';
        next.hourly_rate_adjustment = '';
      }
      if (name === 'is_salaried' && value === 'NO') {
        next.annual_salary = '';
      }
      return next;
    });
  };

  const isSalariedEmployee = form.is_salaried === 'YES';

  const ensurePrimaryRolePositionForEmployee = async (employeeId, rolePositionId, token) => {
    const rolePositionIdTrimmed = String(rolePositionId || '').trim();
    if (!employeeId || !rolePositionIdTrimmed || !tenantId) return { ok: true, skipped: true };

    const authHeaders = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    };

    const listResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}conductor/employee-role-positions/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&employee_id=${encodeURIComponent(employeeId)}`,
      {
        method: 'GET',
        headers: authHeaders,
      }
    );
    if (!listResponse.ok) {
      const errorData = await listResponse.json().catch(() => ({}));
      return {
        ok: false,
        message:
          errorData.error ||
          errorData.message ||
          `Failed to load employee positions: ${listResponse.status}`,
      };
    }

    const existing = await listResponse.json();
    const rows = Array.isArray(existing) ? existing : [];
    const targetId = String(rolePositionIdTrimmed);
    const matching = rows.find((row) => String(row.rolePositionId || '') === targetId);
    const primaryRows = rows.filter((row) => row.isPrimary === true);
    const otherPrimaries = primaryRows.filter(
      (row) => String(row.employeeRolePositionId) !== String(matching?.employeeRolePositionId || '')
    );

    const demoteOthers = async (keepId) => {
      for (const row of rows) {
        if (!row.isPrimary) continue;
        if (String(row.employeeRolePositionId) === String(keepId)) continue;
        const demoteResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/employee-role-positions/${row.employeeRolePositionId}`,
          {
            method: 'PUT',
            headers: authHeaders,
            body: JSON.stringify({
              tenantId,
              employeeId,
              rolePositionId: row.rolePositionId,
              isPrimary: false,
              assignmentType: 'SECONDARY',
              status: row.status || 'ACTIVE',
              updatedBy: username,
              createdBy: row.createdBy || username,
            }),
          }
        );
        if (!demoteResponse.ok) {
          const errorData = await demoteResponse.json().catch(() => ({}));
          throw new Error(
            errorData.error ||
              errorData.message ||
              `Failed to update existing primary position: ${demoteResponse.status}`
          );
        }
      }
    };

    try {
      // Already linked to this position: keep one primary and demote others.
      if (matching?.employeeRolePositionId) {
        if (matching.isPrimary !== true) {
          const promoteResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}conductor/employee-role-positions/${matching.employeeRolePositionId}`,
            {
              method: 'PUT',
              headers: authHeaders,
              body: JSON.stringify({
                tenantId,
                employeeId,
                rolePositionId: matching.rolePositionId,
                isPrimary: true,
                assignmentType: 'PRIMARY',
                status: matching.status || 'ACTIVE',
                updatedBy: username,
                createdBy: matching.createdBy || username,
              }),
            }
          );
          if (!promoteResponse.ok) {
            const errorData = await promoteResponse.json().catch(() => ({}));
            return {
              ok: false,
              message:
                errorData.error ||
                errorData.message ||
                `Failed to set default position: ${promoteResponse.status}`,
            };
          }
        }
        await demoteOthers(matching.employeeRolePositionId);
        return { ok: true, skipped: matching.isPrimary === true && otherPrimaries.length === 0 };
      }

      // Update the current primary row to the new default position.
      if (primaryRows.length > 0) {
        const primary = primaryRows[0];
        const updateResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/employee-role-positions/${primary.employeeRolePositionId}`,
          {
            method: 'PUT',
            headers: authHeaders,
            body: JSON.stringify({
              tenantId,
              employeeId,
              rolePositionId: rolePositionIdTrimmed,
              isPrimary: true,
              assignmentType: 'PRIMARY',
              status: primary.status || 'ACTIVE',
              updatedBy: username,
              createdBy: primary.createdBy || username,
            }),
          }
        );
        if (!updateResponse.ok) {
          const errorData = await updateResponse.json().catch(() => ({}));
          return {
            ok: false,
            message:
              errorData.error ||
              errorData.message ||
              `Failed to update default position: ${updateResponse.status}`,
          };
        }
        await demoteOthers(primary.employeeRolePositionId);
        return { ok: true, skipped: false };
      }

      // No positions yet: create primary.
      const createResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/employee-role-positions`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          tenantId,
          employeeId,
          rolePositionId: rolePositionIdTrimmed,
          isPrimary: true,
          assignmentType: 'PRIMARY',
          status: 'ACTIVE',
          createdBy: username,
          updatedBy: username,
        }),
      });

      if (createResponse.ok) return { ok: true, skipped: false };

      const errorData = await createResponse.json().catch(() => ({}));
      const message =
        errorData.error || errorData.message || `Failed to assign role: ${createResponse.status}`;
      if (createResponse.status === 400 && /already exists/i.test(message)) {
        return { ok: true, skipped: true, duplicate: true };
      }
      return { ok: false, message };
    } catch (error) {
      return { ok: false, message: error.message || 'Failed to sync default position' };
    }
  };

  const createPrimaryEmployeePropertyForEmployee = async (employeeId, propertyId, token) => {
    const propertyIdTrimmed = String(propertyId || '').trim();
    if (!employeeId || !propertyIdTrimmed || !tenantId) return { ok: true, skipped: true };

    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/employee-properties`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tenantId,
        employeeId,
        propertyId: propertyIdTrimmed,
        isPrimary: true,
        status: 'ACTIVE',
        createdBy: username,
        updatedBy: username,
      }),
    });

    if (response.ok) return { ok: true, skipped: false };

    const errorData = await response.json().catch(() => ({}));
    const message = errorData.error || errorData.message || `Failed to assign property: ${response.status}`;
    if (response.status === 400 && /already exists/i.test(message)) {
      return { ok: true, skipped: true, duplicate: true };
    }
    return { ok: false, message };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if ((isEditMode && !canEdit) || (!isEditMode && !canAdd)) return;
    if (form.is_salaried === 'YES' && (form.annual_salary === '' || Number(form.annual_salary) <= 0)) {
      showError('Annual salary is required for salaried employees');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = buildEmployeePayload(form, editingEmployeeId);
      const response = await fetch(
        isEditMode
          ? `${process.env.REACT_APP_BACKEND_URL}conductor/employees/${editingEmployeeId}`
          : `${process.env.REACT_APP_BACKEND_URL}conductor/employees`,
        {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to save employee: ${response.status}`);
      }

      const savedEmployee = await response.json();
      const mappedEmployee = mapApiEmployeeToRow(savedEmployee);
      const savedEmployeeId = savedEmployee?.employeeId || mappedEmployee.employee_uuid;

      const assignErrors = [];
      let roleAssigned = false;
      if (savedEmployeeId) {
        if (!isEditMode) {
          const primaryPropertyId = form.primary_property_id || selectedPropertyId;
          if (primaryPropertyId) {
            const propertyResult = await createPrimaryEmployeePropertyForEmployee(
              savedEmployeeId,
              primaryPropertyId,
              token
            );
            if (!propertyResult.ok) {
              assignErrors.push(`Property assignment failed: ${propertyResult.message}`);
            }
          }
        }

        if (form.default_role_id) {
          const roleResult = await ensurePrimaryRolePositionForEmployee(
            savedEmployeeId,
            form.default_role_id,
            token
          );
          if (!roleResult.ok) {
            assignErrors.push(`Role assignment failed: ${roleResult.message}`);
          } else if (!roleResult.skipped) {
            roleAssigned = true;
          }
        }
      }

      if (isEditMode) {
        setRows((prev) => prev.map((row) => (row.id === mappedEmployee.id ? mappedEmployee : row)));
      } else {
        setRows((prev) => [mappedEmployee, ...prev]);
      }
      handleCloseDialog();

      const hasPrimaryProperty = Boolean(form.primary_property_id || selectedPropertyId);
      if (assignErrors.length) {
        showError(
          `${isEditMode ? 'Employee updated' : 'Employee saved'}. ${assignErrors.join(' ')}`
        );
      } else if (isEditMode) {
        showSuccess(
          roleAssigned
            ? 'Employee updated and default position assigned under Employee Details.'
            : 'Employee updated successfully'
        );
      } else if (hasPrimaryProperty && form.default_role_id) {
        showSuccess(
          'Employee saved; primary business unit and default role assigned in employee business units and role positions.'
        );
      } else if (hasPrimaryProperty) {
        showSuccess('Employee saved and primary business unit assigned in employee business units.');
      } else if (form.default_role_id) {
        showSuccess('Employee saved and default role assigned as primary role position.');
      } else {
        showSuccess('Employee saved successfully');
      }
    } catch (error) {
      console.error('Error creating employee:', error);
      showError(error.message || 'Failed to save employee');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    if (!canDelete || !hasSelectedRow) return;
    const selected = selectedRows.filter((row) => row.employee_uuid);
    if (!selected.length) return;
    setRowsPendingDelete(selected);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setRowsPendingDelete([]);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete || !rowsPendingDelete.length) return;

    setDeleteLoading(true);
    const token = localStorage.getItem('token');
    const idToRowId = new Map(rowsPendingDelete.map((row) => [String(row.employee_uuid), row.id]));
    const ids = rowsPendingDelete.map((row) => row.employee_uuid);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/employees/bulk-delete`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(ids),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to delete employees: ${response.status}`);
      }

      const result = await response.json();
      const succeeded = Array.isArray(result.succeeded) ? result.succeeded.map(String) : [];
      const failed = Array.isArray(result.failed) ? result.failed : [];
      const deletedRowIds = succeeded.map((id) => idToRowId.get(id)).filter(Boolean);

      if (deletedRowIds.length) {
        setRows((prev) => prev.filter((row) => !deletedRowIds.includes(row.id)));
        setSelectionModel((prev) => prev.filter((id) => !deletedRowIds.includes(id)));
      }

      setDeleteDialogOpen(false);
      setRowsPendingDelete([]);

      const okCount = result.successCount ?? succeeded.length;
      const failCount = result.failureCount ?? failed.length;

      if (failCount === 0) {
        showSuccess(okCount === 1 ? 'Employee deleted successfully' : `${okCount} employees deleted successfully`);
      } else if (okCount === 0) {
        showError(failed.map((f) => f.error || 'Delete failed').join('; '));
      } else {
        showError(
          `Deleted ${okCount}, failed ${failCount}: ${failed.map((f) => f.error || 'Delete failed').join('; ')}`
        );
      }
    } catch (error) {
      console.error('Error bulk deleting employees:', error);
      showError(error.message || 'Failed to delete employees');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEmployeeRowClick = (params) => {
    setSelectedDrawerEmployeeRow(params.row);
    setEmployeeDrawerOpen(true);
  };

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{ fontSize: '0.9rem', '& .MuiBreadcrumbs-separator': { color: ht.white } }}
        >
          <Link
            underline="hover"
            onClick={onBack}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: ht.white,
              '&:hover': { color: ht.white },
            }}
          >
            Workforce Operations
          </Link>
          <Typography sx={{ color: ht.white }}>
            {selectedBusinessUnitLabel ? `Employee - ${selectedBusinessUnitLabel}` : 'Employee'}
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Employee
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canAdd ? 'Add Employee' : 'No add permission'}>
            <span>
              <IconButton sx={harmonatePlainIconButtonSx} onClick={handleOpenDialog} disabled={!canAdd}>
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={!canEdit ? 'No edit permission' : hasSingleSelection ? 'Edit Employee' : hasSelectedRow ? 'Select one employee to edit' : 'Edit Employee'}>
            <span>
            <IconButton sx={harmonatePlainIconButtonSx} disabled={!hasSingleSelection || !canEdit} onClick={handleEditSelected}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={!canDelete ? 'No delete permission' : hasSelectedRow && selectedRows.length > 1 ? `Delete ${selectedRows.length} Employees` : 'Delete Employee'}>
            <span>
              <IconButton sx={harmonatePlainIconButtonSx} disabled={!hasSelectedRow || !canDelete} onClick={handleOpenDeleteDialog}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={!canUpload ? 'No upload permission' : selectedPropertyId ? 'Import from Excel or CSV' : 'Select a business unit to import'}>
            <span>
              <IconButton
                sx={harmonatePlainIconButtonSx}
                disabled={!selectedPropertyId || !canUpload}
                onClick={() => {
                  if (!canUpload) return;
                  setEmployeeImportOpen(true);
                }}
              >
                <UploadFileIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          flex: 1,
          mb: 3,
          height: 500,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGridPro
          rows={rows}
          columns={employeeColumns}
          getRowId={(row) => row.id}
          rowHeight={40}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
          pagination
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          autoPageSize={false}
          loading={loadingEmployees}
          onRowClick={handleEmployeeRowClick}
          sx={{ ...harmonateDataGridSx, '& .MuiDataGrid-row:hover': { ...harmonateDataGridSx['& .MuiDataGrid-row:hover'], cursor: 'pointer' } }}
        />
      </Paper>

      <EmployeeDrawer
        open={employeeDrawerOpen}
        onClose={() => setEmployeeDrawerOpen(false)}
        selectedEmployee={selectedDrawerEmployeeRow}
        selectedBusinessUnit={selectedBusinessUnit}
        onAvailabilitySaved={fetchEmployees}
        onLeaveChanged={onEmployeeDataChanged}
      />

      <EmployeeImportDialog
        open={employeeImportOpen}
        onClose={() => setEmployeeImportOpen(false)}
        tenantId={tenantId}
        primaryPropertyId={selectedPropertyId}
        primaryPropertyName={selectedBusinessUnitName}
        username={username}
        showSuccess={showSuccess}
        showError={showError}
        onImportSuccess={fetchEmployees}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{isEditMode ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <Typography variant="subtitle2" sx={{ color: ht.text, fontWeight: 600 }}>
              Employee Information
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Employee Code" name="employee_code" value={form.employee_code} onChange={handleFormChange} fullWidth size="small" required />
              <TextField
                label="Primary Business Unit"
                name="primary_property_id"
                value={selectedBusinessUnitName || form.primary_property_id}
                fullWidth
                size="small"
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="First Name" name="first_name" value={form.first_name} onChange={handleFormChange} fullWidth size="small" required />
              <TextField label="Last Name" name="last_name" value={form.last_name} onChange={handleFormChange} fullWidth size="small" required />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Mobile Phone" name="phone" value={form.phone} onChange={handleFormChange} fullWidth size="small" />
              <TextField label="Home Phone" name="home_phone" value={form.home_phone} onChange={handleFormChange} fullWidth size="small" />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Default Position"
                name="default_role_id"
                value={form.default_role_id}
                onChange={handleFormChange}
                fullWidth
                size="small"
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                disabled={roleMastersLoading || !selectedPropertyId}
              >
                <MenuItem value="">
                  <em>Select default position</em>
                </MenuItem>
                {unknownDefaultRoleId ? (
                  <MenuItem value={unknownDefaultRoleId}>
                    Current value (not in list)
                  </MenuItem>
                ) : null}
                {roleMasters.map((role) => {
                  const rid = getRolePositionId(role);
                  if (!rid) return null;
                  return (
                    <MenuItem key={rid} value={String(rid)}>
                      {getRolePositionLabel(role)}
                    </MenuItem>
                  );
                })}
              </TextField>
              <TextField
                label="Employment Type"
                name="employment_type"
                value={form.employment_type}
                onChange={handleFormChange}
                fullWidth
                size="small"
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              >
                {employmentTypeOptions.map((opt) => (
                  <MenuItem key={opt.code} value={opt.code}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Department"
                name="department"
                value={form.department}
                fullWidth
                size="small"
                disabled
                InputLabelProps={{ shrink: true }}
                helperText="Filled from Default Position"
              />
              <TextField
                label="Department Code"
                name="department_code"
                value={form.department_code}
                fullWidth
                size="small"
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Jobcode Level 1"
                name="jobcode_level_1"
                value={form.jobcode_level_1}
                fullWidth
                size="small"
                disabled
                InputLabelProps={{ shrink: true }}
                helperText="Filled from Position Job Code"
              />
              <TextField
                label="Jobcode Level 2"
                name="jobcode_level_2"
                value={form.jobcode_level_2}
                fullWidth
                size="small"
                disabled
                InputLabelProps={{ shrink: true }}
                helperText="Filled from Business Unit / Property"
              />
            </Box>

            <TextField label="Hire Date" name="hire_date" type="date" value={form.hire_date} onChange={handleFormChange} fullWidth size="small" InputLabelProps={{ shrink: true }} />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Status" name="status" value={form.status} onChange={handleFormChange} fullWidth size="small" select SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                <MenuItem value="ON_LEAVE">ON LEAVE</MenuItem>
                <MenuItem value="TERMINATED">TERMINATED</MenuItem>
              </TextField>
              <TextField label="Max Hours / Week" name="max_hours_per_week" type="number" value={form.max_hours_per_week} onChange={handleFormChange} fullWidth size="small" />
            </Box>

            {isSalariedEmployee ? (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Is Salaried?" name="is_salaried" value={form.is_salaried} onChange={handleFormChange} fullWidth size="small" select required SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
                  <MenuItem value="NO">NO</MenuItem>
                  <MenuItem value="YES">YES</MenuItem>
                </TextField>
                <TextField
                  label="Annual Salary"
                  name="annual_salary"
                  type="number"
                  value={form.annual_salary}
                  onChange={handleFormChange}
                  fullWidth
                  size="small"
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                />
                <TextField
                  label="Is Overtime Allowed"
                  name="is_overtime_allowed"
                  value="NO"
                  onChange={handleFormChange}
                  fullWidth
                  size="small"
                  select
                  SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                  disabled
                >
                  <MenuItem value="YES">YES</MenuItem>
                  <MenuItem value="NO">NO</MenuItem>
                </TextField>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Is Salaried?" name="is_salaried" value={form.is_salaried} onChange={handleFormChange} fullWidth size="small" select required SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
                    <MenuItem value="NO">NO</MenuItem>
                    <MenuItem value="YES">YES</MenuItem>
                  </TextField>
                  <TextField label="Hourly Rate (Base)" name="hourly_rate" type="number" value={form.hourly_rate} onChange={handleFormChange} fullWidth size="small" inputProps={{ min: 0, step: 0.01 }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {!isEditMode && (
                    <TextField label="Hourly Rate Adjustment" name="hourly_rate_adjustment" type="number" value={form.hourly_rate_adjustment} onChange={handleFormChange} fullWidth size="small" inputProps={{ step: 0.01 }} />
                  )}
                  <TextField
                    label="Is Overtime Allowed"
                    name="is_overtime_allowed"
                    value={form.is_overtime_allowed}
                    onChange={handleFormChange}
                    fullWidth
                    size="small"
                    select
                    SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                  >
                    <MenuItem value="YES">YES</MenuItem>
                    <MenuItem value="NO">NO</MenuItem>
                  </TextField>
                </Box>
              </>
            )}

            <TextField
              label="Notes"
              name="notes"
              value={form.notes}
              onChange={handleFormChange}
              fullWidth
              size="small"
              multiline
              minRows={3}
            />
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting} sx={harmonateProceedButtonSx}>
              {submitting ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          {rowsPendingDelete.length <= 1 ? (
            <Typography sx={{ color: ht.text, fontSize: '0.875rem' }}>
              Are you sure you want to delete the employee &quot;
              {rowsPendingDelete[0]?.full_name ||
                [rowsPendingDelete[0]?.first_name, rowsPendingDelete[0]?.last_name].filter(Boolean).join(' ') ||
                rowsPendingDelete[0]?.employee_code ||
                'selected'}
              &quot;?
            </Typography>
          ) : (
            <Typography sx={{ color: ht.text, fontSize: '0.875rem' }}>
              Are you sure you want to delete <strong>{rowsPendingDelete.length}</strong> selected employees?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={deleteLoading}
            sx={harmonateAbortButtonSx}
          >
            {deleteLoading
              ? 'Deleting...'
              : rowsPendingDelete.length > 1
                ? `Delete ${rowsPendingDelete.length}`
                : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </>
  );
};

export default Employee;
